<template>
  <BFlex
    v-loading="settingLoading"
    class="login-email-confirm"
  >
    <template v-if="!settingLoading">
      <div v-if="success">
        <BAlert
          :text="i18n.t('general.done.withTarget', { action: i18n.t('general.update.text'), target: i18n.t('loginEmail.title') })"
          class="success"
          type="success"
          show-icon
        />
      </div>
      <div v-else>
        <BAlert
          :text="i18n.t('general.fail.withTarget', { action: i18n.t('general.update.text'), target: i18n.t('loginEmail.title') })"
          class="alert"
          type="error"
          show-icon
        />
      </div>
      <div class="mt-600">
        <BBtn
          v-if="success"
          :disabled="settingLoading"
          :loading="settingLoading"
          @click="toSignIn"
        >
          {{ $t('loginEmail.toLoginPage') }}
        </BBtn>
        <BBtn
          v-else
          :disabled="settingLoading"
          :loading="settingLoading"
          @click="toFallback"
        >
          {{ $t('loginEmail.toTopPage') }}
        </BBtn>
      </div>
    </template>
  </BFlex>
</template>

<script lang="ts" setup>
import { onBeforeMount, ref, computed } from 'vue';
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router';
import { UserApiService } from '@/api/user/resources/user';
import { useWait } from '@/composable/vue-wait';
import { i18nGlobal as i18n } from '@/i18n';
import { bitterAlert } from '@/plugins/BBitterAlert';

const props = defineProps<{
  signInTo: RouteLocationRaw;
  fallbackTo: RouteLocationRaw;
}>();

const success = ref<boolean>(false);
const route = useRoute();
const router = useRouter();
const {
  wait,
  doActionWithWait,
} = useWait();

onBeforeMount(async () => {
  const confirmationToken = route.params.token as string;
  confirmLoginEmailUpdate(confirmationToken);
});

const settingLoading = computed(() => wait.is(['confirmLoginEmailUpdateWait']));

const confirmLoginEmailUpdate = async (confirmationToken: string) => {
  const api = new UserApiService();
  await doActionWithWait('confirmLoginEmailUpdateWait', async () => {
    await api.confirmLoginEmailUpdate({
      request: {
        confirmLoginEmailUpdateBody: {
          confirmationToken,
        },
      },
      errorHandlers: {
        404: errorHandler,
        422: errorHandler,
      },
    });
  });
  success.value = true;
};

const errorHandler = () => {
  success.value = false;
  bitterAlert.show({
    title: i18n.t('changeEmail.confirmFailed.title'),
    text: i18n.t('changeEmail.confirmFailed.message', { itemName: i18n.t('user.loginEmail') }),
    buttonsCancel: false,
  });
};

const toSignIn = () => {
  router.push(props.signInTo);
};
const toFallback = () => {
  router.push(props.fallbackTo);
};
</script>

<style lang="scss" scoped>
  .login-email-confirm {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    // NOTE: 要素の高さの約半分が50px
    margin-top: calc(50vh - 50px);
  }
</style>
